// Curated skill aliases so the fit score treats common synonyms as the same
// skill (e.g. a résumé listing "JS" matches a job asking for "JavaScript").
// Keys are the canonical lowercase form; values list every accepted lowercase
// variant (including the canonical). Only unambiguous aliases are included —
// short/common tokens like "go", "r", "py" are deliberately omitted to avoid
// false positives when scanning free-text descriptions.
export const SKILL_ALIASES: Record<string, string[]> = {
  javascript: ['javascript', 'js'],
  typescript: ['typescript', 'ts'],
  react: ['react', 'reactjs', 'react.js'],
  'react native': ['react native', 'react-native'],
  'node.js': ['node.js', 'nodejs'],
  postgresql: ['postgresql', 'postgres', 'psql'],
  kubernetes: ['kubernetes', 'k8s'],
  'c#': ['c#', 'csharp'],
  'c++': ['c++', 'cpp'],
  aws: ['aws', 'amazon web services'],
  gcp: ['gcp', 'google cloud platform', 'google cloud'],
  'ci/cd': ['ci/cd', 'cicd'],
};

const normalize = (s: string): string => s.toLowerCase().trim();

// variant -> canonical, built once from SKILL_ALIASES.
const VARIANT_TO_CANONICAL: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const [canonical, variants] of Object.entries(SKILL_ALIASES)) {
    for (const v of variants) map[normalize(v)] = canonical;
  }
  return map;
})();

// Map any skill spelling to its canonical form (or itself when unknown).
// Used to de-dupe résumé skills so "JS" and "JavaScript" count once.
export const canonicalSkill = (skill: string): string => {
  const n = normalize(skill);
  return VARIANT_TO_CANONICAL[n] ?? n;
};

// Every term to search for when matching a skill against a job: the skill
// itself plus all known aliases of its canonical group.
export const expandSkillTerms = (skill: string): string[] => {
  const n = normalize(skill);
  const canonical = VARIANT_TO_CANONICAL[n];
  if (canonical) return Array.from(new Set([...SKILL_ALIASES[canonical], n]));
  return [n];
};

// Add dummy default export for Expo Router (every file under app/ needs one).
export default { SKILL_ALIASES, canonicalSkill, expandSkillTerms };
